/**
 * Deployment-environment configuration for the presentation site.
 * PRD §5.1 / §16.1 / §13 row 43 (2026-10-02): everything is configuration — nothing hard-coded.
 * Every deployment instance (possibly several per repository, one per agency domain)
 * gets its own values at deployment stage.
 */
interface AppEnv {
  /** Public API base of the platform server exposing the anonymous public registration API. */
  apiBaseUrl: string;
  /**
   * Agency portal — the site where the FULL Sommet360 app is deployed.
   * **Deployment fallback only** for the "Already registered? / Check my status" links:
   * once an agency is selected, the links resolve to that agency's contract
   * **InternalProviderSite** (PRD §4.1 — the big-platform sign-in site; PublicProviderSite is
   * the presentation site itself, i.e. this deployment). Used before an agency is picked or
   * when the contract has no internal site.
   */
  agencyPortalUrl: string;
  telegramBotUsername: string;
  /** THE INBOUND NUMBER env fallback (`VITE_WHATSAPP_INBOUND_NUMBER`, chatbot PRD §15): the WABA
   *  sender digits the candidates message; the server payload (WhatsAppInboundNumber) takes
   *  precedence — this is only for deployments whose server predates the config payload. */
  whatsappInboundNumber: string;
  /** Branding fallbacks (the agency record from the server takes precedence where provided). */
  agencyName: string;
  agencyDomain: string;
  heroTitle: string;
  /**
   * Terms-of-use document (T-SA-19.01, PRD §19.1 / §17 row 18): the landing checkbox and footer
   * link point here. Empty = the document does not exist yet → the link stays a placeholder.
   */
  termsOfUseUrl: string;
  /** The privacy policy behind the landing checkbox. Empty = a placeholder link (not written yet). */
  privacyPolicyUrl: string;
  /**
   * Page-owning staffing agency GUID — **fallback only** (PRD §5.1, 2026-10-02): the wizard
   * now scopes everything by the agency **selected on the landing page**; this deployment
   * value is used only when nothing is selected (or no server lookup result).
   */
  pageAgencyId: string;
  /**
   * Company account GUID provisioned for that agency (Simple-management account, PRD §4.8) —
   * sent as request.CurrentCompanyAccount of the anonymous registration (server fallback route).
   */
  pageAgencyCompanyAccountId: string;
}

const env = import.meta.env as Record<string, string | undefined>;

/** Read a VITE_* variable: the build-injected env, with a process.env fallback — vitest's
 * `vi.stubEnv` sets both, so tests can flip LIVE/demo mode per test (PRD §5.1 demo mode).
 * The `typeof process` guard matters: the presentation site runs in real browsers. */
const envValue = (name: string): string | undefined =>
  env[name] ?? (typeof process !== "undefined" ? process.env?.[name] : undefined);

export const config: AppEnv = {
  apiBaseUrl: (envValue("VITE_API_BASE_URL") || "").replace(/\/$/, ""),
  // VITE_AGENCY_PORTAL_URL — where the full Sommet360 app is deployed (deployment fallback
  // for the status/login links; normally overridden by the selected agency's internal site).
  agencyPortalUrl: envValue("VITE_AGENCY_PORTAL_URL") || "https://app.example.com/login",

  /** Telegram bot @handle WITHOUT @ — FALLBACK ONLY since 2026-10-03: the preferred-chatbot
   *  flow builds the /start deep link (t.me/{telegramBotUsername}?start={bindingToken}) from
   *  the SERVER payload (GetSiteChatbotConfig → { telegramBotUsername, telegramEnabled });
   *  this env value is used only when the server predates the endpoint or is unreachable.
   *  Empty (server unset + env unset) = the Telegram choice is hidden (channel not offered). */
  telegramBotUsername: envValue("VITE_TELEGRAM_BOT_USERNAME") || "",
  whatsappInboundNumber: envValue("VITE_WHATSAPP_INBOUND_NUMBER") || "",
  // NO hardcoded agency branding: the site shows the agencies the SERVER returns
  // (2026-10-02 ruling) — an unavailable/empty API means "No agency available".
  // Set VITE_AGENCY_NAME / VITE_AGENCY_DOMAIN only as a specific-instance fallback.
  agencyName: envValue("VITE_AGENCY_NAME") || "",
  agencyDomain: envValue("VITE_AGENCY_DOMAIN") || "",
  heroTitle: envValue("VITE_HERO_TITLE") || "",
  termsOfUseUrl: envValue("VITE_TERMS_OF_USE_URL") || "",
  privacyPolicyUrl: envValue("VITE_PRIVACY_POLICY_URL") || "",
  pageAgencyId: envValue("VITE_PAGE_AGENCY_ID") || "",
  pageAgencyCompanyAccountId: envValue("VITE_PAGE_AGENCY_COMPANY_ACCOUNT_ID") || "",
};

/** True when no backend API is configured — the UI then runs on bundled demo data. */
export const isLiveApi = (config.apiBaseUrl ?? "").length > 0;